/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import colors from '@sportninja/common/constants/appColors'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import { useOrganization } from '@sportninja/common/hooks/useOrganization'
import {
  isScheduleNode,
  isTeamNode,
} from '@sportninja/common/features/competition/operations/utils/nodeTypes'
import type { OperationsNode } from '@sportninja/common/features/competition/operations/types/operations.types'
import { findNodeParent } from '@sportninja/common/features/competition/operations/utils/findNodeParent'
import { zIndex } from 'src/components/css'
import { useFullscreen } from 'src/hooks/useFullscreen'
import { useOperationsTree } from './hooks/useOperationsTree'
import { fullscreenModalHostZIndex } from './utils/fullscreenModalHostZIndex'
import { useFillViewportHeight } from './hooks/useFillViewportHeight'
import { useSuppressWindowScroll } from './hooks/useSuppressWindowScroll'
import { useTreeSearch } from './hooks/useTreeSearch'
import { useTreeSelection } from './hooks/useTreeSelection'
import { useOpenAddTeamDeepLink } from './hooks/useOpenAddTeamDeepLink'
import { useInitialTreeState, collectIds } from './hooks/useInitialTreeState'
import { useOperationsDnd } from './hooks/useOperationsDnd'
import OperationsTree from './components/tree/OperationsTree'
import TreeSearchBar from './components/tree/TreeSearchBar'
import TreeSkeleton from './components/tree/TreeSkeleton'
import DetailPanel from './components/panel/DetailPanel'
import PanelSkeleton from './components/panel/PanelSkeleton'
import TeamRosterTab from './components/team/TeamRosterTab'
import type { RosterCounts, RosterTab } from './components/roster/RosterSection'
import {
  LOADING_ROSTER_CAPABILITIES,
  type RosterCapabilities,
} from './hooks/useRosterPermissions'
import RegistrationStrip from './components/RegistrationStrip'
import EditEntityLauncher from './components/EditEntityLauncher'
import DeleteNodeModal from './components/DeleteNodeModal'
import type { PendingCreate } from './types/pendingCreate'
import type { RowAction, DivisionTeamRow } from './grid/useDivisionTeamsColumns'
import type {
  DivisionRowAction,
  SeasonDivisionRow,
} from './grid/useSeasonDivisionsColumns'

interface OperationsTabProps {
  scheduleId: string
  orgId: string
  canEdit: boolean
  /** Sport of the competition — drives the roster's position options. */
  sportId: string | null
  /**
   * Generic-sport competitions filter the team search by this instead of
   * `sportId`. Legacy passes it to the same search; without it, Generic-sport
   * competitions list teams from every sport.
   */
  genericSportId?: string | null
  /**
   * Canlan-only registration/Salesforce fields on the division form. Mirrors
   * the flag the legacy Setup tab receives, so both create identical payloads.
   */
  showSalesforceSettings?: boolean
  isRegistrationOn: boolean
  onToggleRegistration: (next: boolean) => void
}

/**
 * The Operations tab shell. Owns only selection, expansion and which modal is
 * open — every other concern lives in a hook or a child component.
 */
const OperationsTab = ({
  scheduleId,
  orgId,
  canEdit,
  sportId,
  genericSportId = null,
  showSalesforceSettings,
  isRegistrationOn,
  onToggleRegistration,
}: OperationsTabProps) => {
  'use memo'

  const queryClient = useQueryClient()
  // Fills the viewport below this tab's top edge so the rail and panel scroll
  // internally instead of growing the page.
  // No bottom gutter: the tab paints its own background to the bottom edge, so
  // the default 24px reads as a stray gap rather than as breathing room.
  const fill = useFillViewportHeight(0)
  // The tab owns its scrolling; the shared page wrappers around it are
  // content-sized and would otherwise add a second, redundant scrollbar.
  useSuppressWindowScroll()
  const { isFullscreen, toggle: toggleFullscreen } = useFullscreen()
  const { nodes, isLoading } = useOperationsTree(scheduleId)
  const search = useTreeSearch(nodes)
  // Only the root schedule carries an `organization` block, and the tree is
  // always rooted at this competition — so the root's name pairs with the
  // `orgId` prop to identify the owning org.
  const rootNode = nodes[0]
  const organization =
    rootNode && isScheduleNode(rootNode) && rootNode.organizationName
      ? { id: orgId, name: rootNode.organizationName }
      : null
  // The tree only carries the org's name; the roster Add gates additionally
  // need `sync_salesforce`, so read the organization itself.
  const { organization: orgDetails, isLoading: isOrgLoading } =
    useOrganization(orgId)
  const selection = useTreeSelection(search.nodes)
  // Drag controller for the rail. Resolves drops against the FULL tree, not the
  // search-filtered one, so a drop never derives its intent from a partial
  // sibling list.
  const dnd = useOperationsDnd({ nodes, rootScheduleId: scheduleId })

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const [editingNode, setEditingNode] = useState<OperationsNode | null>(null)
  const [pendingCreate, setPendingCreate] = useState<PendingCreate | null>(null)
  const [deletingNode, setDeletingNode] = useState<OperationsNode | null>(null)
  // Incremented by the panel header's Add Player button. The button lives in the
  // header (beside Edit Team) but the modal must live inside RosterProvider, so a
  // counter carries the click across that boundary and still fires on a repeat.
  const [addPlayerSignal, setAddPlayerSignal] = useState(0)
  // Same round trip as `addPlayerSignal`, in reverse: the Import button lives
  // in the header, outside RosterProvider, so a successful import bumps this
  // counter to make the provider refetch the roster it just changed.
  const [rosterRefreshKey, setRosterRefreshKey] = useState(0)
  // Roster counts for the panel's stat strip, reported up from RosterSection —
  // the strip renders in PanelHeader, outside RosterProvider.
  const [rosterCounts, setRosterCounts] = useState<RosterCounts | null>(null)
  // Which roster tab is visible, so the header's Add button reads Add Player or
  // Add Team Official. Same round trip as the counts — the button renders in
  // PanelHeader, outside RosterProvider.
  const [rosterTab, setRosterTab] = useState<RosterTab>('players')
  // Defaults to loading — canAddPlayer/canAddOfficial false, isLoading true —
  // so the Add button errs closed AND shows a skeleton rather than nothing on
  // the render before `TeamRosterTab`/`RosterSection` report in.
  const [rosterCapabilities, setRosterCapabilities] =
    useState<RosterCapabilities>(LOADING_ROSTER_CAPABILITIES)

  useOpenAddTeamDeepLink({
    nodes,
    isLoading,
    isRegistrationOn,
    onTrigger: (division) => {
      selection.setSelectedId(division.id)
      setPendingCreate({ kind: 'team', parent: division })
    },
  })

  // First load only: expand every node and select the root season, so the
  // user lands on a populated tree + panel instead of a collapsed rail and
  // an empty right side. Guarded to fire once — the tree query invalidates
  // after every mutation, and re-expanding/re-selecting on each of those
  // would fight the user's own subsequent collapse/expand.
  useInitialTreeState({
    nodes,
    isLoading,
    onInitialise: (ids, rootId) => {
      setExpandedIds(new Set(ids))
      selection.setSelectedId(rootId)
    },
  })

  const toggleExpanded = (id: string) => {
    setExpandedIds((current) => {
      const next = new Set(current)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  // Expand-all uses the FULL tree, not the search-filtered one, so the rail is
  // fully open once the term is cleared rather than open only where it matched.
  const toggleAll = () => {
    setExpandedIds((current) =>
      current.size === 0 ? new Set(collectIds(nodes)) : new Set()
    )
  }

  // While searching, every surviving branch is shown expanded so matches are
  // visible without the user re-opening each ancestor.
  const effectiveExpanded = search.isSearching
    ? new Set(collectIds(search.nodes))
    : expandedIds

  const invalidateTree = () =>
    queryClient.invalidateQueries({
      queryKey: queryKeys.operations.tree(scheduleId),
    })

  // Org-wide imports can add teams (the Add Team search) and change any team's
  // roster, so everything cached from either is refetched — this tab's roster
  // lists sit under `operations.all`, the Team page's under `teamRosters.all`.
  const handleOrgImported = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.operations.all })
    queryClient.invalidateQueries({ queryKey: queryKeys.teamSearch.all })
    queryClient.invalidateQueries({ queryKey: queryKeys.teamRosters.all })
  }

  // The Org screen's Teams-tab gate: the org's own `update`, not the
  // competition's. Kept while loading so the buttons show a skeleton rather
  // than popping in once the permission lands.
  const orgTransfer =
    isOrgLoading || orgDetails?.permission.update
      ? {
          orgId,
          orgName: orgDetails?.name_full || orgDetails?.name || '',
          isLoading: isOrgLoading,
          onImported: handleOrgImported,
          isRegistrationOn,
        }
      : null

  const selectedNode = selection.selectedNode
  const divisionScheduleId =
    selectedNode && isTeamNode(selectedNode) ? selectedNode.scheduleId : null

  // `TeamRosterTab` returns early — skeleton, error, or "no roster for
  // division" — before `RosterSection` ever mounts, so those states never
  // report capabilities. Without this reset, the PREVIOUS team's (or no
  // team's) capabilities would leak into the newly selected node's Add
  // button, defeating "errs closed" for every selection after the first.
  // Keyed on the node id so it fires for every selection change — including
  // team-to-division and division-to-division, not just team-to-team.
  //
  // Resets to the LOADING state (isLoading: true), not the denied state —
  // TeamRosterTab's own loading skeleton is about to replace whatever else is
  // on screen, so the header's Add button should show a skeleton too. Both
  // TeamRosterTab's error and no-roster returns explicitly clear isLoading
  // below (see their effects) so a division with no roster or a failed fetch
  // still lands on "render nothing" instead of a stuck skeleton.
  useEffect(() => {
    setRosterCapabilities(LOADING_ROSTER_CAPABILITIES)
  }, [selectedNode?.id])

  const handleTeamRowAction = (action: RowAction, row: DivisionTeamRow) => {
    if (action === 'edit') {
      setEditingNode(row)
      return
    }
    if (action === 'delete') {
      setDeletingNode(row)
      return
    }
    selection.setSelectedId(row.id)
  }

  const handleDivisionRowAction = (
    action: DivisionRowAction,
    row: SeasonDivisionRow
  ) => {
    if (action === 'edit') {
      setEditingNode(row)
      return
    }
    if (action === 'delete') {
      setDeletingNode(row)
      return
    }
    selection.setSelectedId(row.id)
  }

  return (
    <div
      ref={fill.ref}
      css={[tabStyle, isFullscreen && fullscreenStyle]}
      // The shell sizes itself to the window, so the measured height would
      // only fight it.
      style={!isFullscreen && fill.height ? { height: fill.height } : undefined}
    >
      <RegistrationStrip
        isRegistrationOn={isRegistrationOn}
        canEdit={canEdit}
        onToggle={onToggleRegistration}
      />

      <div css={bodyStyle}>
        <div css={railStyle}>
          <TreeSearchBar
            term={search.term}
            onTermChange={search.setTerm}
            matchCount={search.matchCount}
            isSearching={search.isSearching}
            isAllCollapsed={expandedIds.size === 0}
            onToggleAll={toggleAll}
            isFullscreen={isFullscreen}
            onToggleFullscreen={toggleFullscreen}
          />
          {isLoading ? (
            <TreeSkeleton />
          ) : (
            <OperationsTree
              nodes={search.nodes}
              selectedId={selection.selectedId}
              expandedIds={effectiveExpanded}
              matchedIds={search.matchedIds}
              canEdit={canEdit}
              isRegistrationOn={isRegistrationOn}
              onDrop={dnd.handleDrop}
              pendingMove={dnd.pendingMove}
              pendingIds={dnd.pendingIds}
              onSelect={selection.setSelectedId}
              onToggle={toggleExpanded}
              onAdd={(node, kind) => {
                // The tree only calls onAdd for a node canAddChild allows,
                // which is never true for a team — this is always a
                // schedule node in practice.
                if (isScheduleNode(node)) {
                  setPendingCreate({ kind, parent: node })
                }
              }}
              onEdit={setEditingNode}
              onDelete={setDeletingNode}
            />
          )}
        </div>

        {isLoading ? (
          <PanelSkeleton />
        ) : selectedNode ? (
          <DetailPanel
            node={selectedNode}
            ancestors={selection.ancestors}
            organization={organization}
            rootScheduleId={scheduleId}
            canEdit={canEdit}
            onSelectNode={selection.setSelectedId}
            onEdit={setEditingNode}
            onAddChild={(node, kind) => {
              setPendingCreate({ kind, parent: node })
            }}
            onTeamRowAction={handleTeamRowAction}
            onDivisionRowAction={handleDivisionRowAction}
            onAddPlayer={() => setAddPlayerSignal((n) => n + 1)}
            rosterCounts={rosterCounts}
            rosterTab={rosterTab}
            canAddPlayer={rosterCapabilities.canAddPlayer}
            canAddOfficial={rosterCapabilities.canAddOfficial}
            isRosterLoading={rosterCapabilities.isLoading}
            tree={nodes}
            canImportRoster={rosterCapabilities.canImportRoster}
            importDisabledReason={rosterCapabilities.importDisabledReason}
            canExportRoster={rosterCapabilities.canExportRoster}
            rosterId={rosterCapabilities.rosterId}
            onRosterImported={() => setRosterRefreshKey((n) => n + 1)}
            isRegistrationOn={isRegistrationOn}
            dnd={dnd}
            orgTransfer={orgTransfer}
            rosterSlot={
              isTeamNode(selectedNode) ? (
                <TeamRosterTab
                  // Keyed so switching teams remounts and the counts below are
                  // re-reported, never carried over from the previous team.
                  key={selectedNode.id}
                  teamId={selectedNode.teamId}
                  teamName={selectedNode.name}
                  divisionScheduleId={divisionScheduleId}
                  sportId={sportId}
                  canEdit={canEdit}
                  organization={orgDetails}
                  addPlayerSignal={addPlayerSignal}
                  refreshKey={rosterRefreshKey}
                  onCountsChange={setRosterCounts}
                  onTabChange={setRosterTab}
                  onCapabilitiesChange={setRosterCapabilities}
                  hostZIndex={fullscreenModalHostZIndex(isFullscreen)}
                />
              ) : null
            }
          />
        ) : (
          <div css={placeholderStyle} />
        )}
      </div>

      <EditEntityLauncher
        node={editingNode}
        pendingCreate={pendingCreate}
        orgId={orgId}
        rootScheduleId={scheduleId}
        showSalesforceSettings={showSalesforceSettings}
        sportId={sportId}
        genericSportId={genericSportId}
        editParent={editingNode ? findNodeParent(nodes, editingNode.id) : null}
        onClose={() => {
          setEditingNode(null)
          setPendingCreate(null)
        }}
        onSaved={invalidateTree}
      />

      <DeleteNodeModal
        node={deletingNode}
        rootScheduleId={scheduleId}
        onClose={() => setDeletingNode(null)}
        onDeleted={invalidateTree}
      />
    </div>
  )
}

/**
 * The two panes scroll internally rather than growing the page. The height is
 * set inline from `useFillViewportHeight` (measured, not a hardcoded offset —
 * see that hook); this is the fallback for the first paint before it lands.
 */
const tabStyle = css`
  display: flex;
  flex-direction: column;
  /* No min-height: it would fight the measured height on a short viewport.
     The hook applies its own floor. */
  background: ${colors.SECONDARY_500};
  /* The measured height is a hard box — children scroll inside it. */
  overflow: hidden;
`

/**
 * Lifts the whole tab over the window, covering the nav, the competition header
 * and the tab bar — the same shell as the registration page's full screen.
 *
 * `position: fixed` rather than `requestFullscreen()`: the edit/add/delete
 * modals and every dropdown portal into `document.body`, outside any element
 * the Fullscreen API would promote, so they would not render. Sits one step
 * above `zIndex.header` and well below the modal layer, so modals keep
 * painting over it.
 */
const fullscreenStyle = css`
  position: fixed;
  inset: 0;
  z-index: ${zIndex.overlay};
`

const bodyStyle = css`
  display: grid;
  grid-template-columns: 384px 1fr;
  flex: 1;
  min-height: 0;

  /* A grid ITEM defaults to min-height: auto and so refuses to shrink below its
     content. Pinning the row to the track height is what lets the rail and the
     panel scroll internally instead of stretching the tab. */
  grid-template-rows: minmax(0, 1fr);

  > * {
    min-height: 0;
    min-width: 0;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

const railStyle = css`
  display: flex;
  flex-direction: column;
  min-width: 0;
  /* Lets the tree's own overflow:auto engage instead of the rail growing. */
  min-height: 0;
  border-right: 1px solid ${colors.SECONDARY_200};
  background: ${colors.SECONDARY_400};
`

const placeholderStyle = css`
  background: ${colors.SECONDARY_500};
`

export default OperationsTab
